import React, { useRef, useCallback } from 'react';
import { motion } from 'framer-motion';
import styled from 'styled-components';
import ThumbnailCard from './ThumbnailCard';
import SectionControls from './SectionControls';
import { getContentByIds } from '../data/mockData';

const RowContainer = styled.section`
  margin-bottom: 3rem;
  padding: 0 4%;

  @media (max-width: 768px) {
    margin-bottom: 2rem;
    padding: 0 20px;
  }
`;

const RowHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 0.5rem;

  &:hover ${props => props.$hasControls ? 'div' : 'none'} {
    opacity: 1;
  }
`;

const RowTitle = styled.h2`
  font-size: 1.4rem;
  font-weight: 700;
  color: #e5e5e5;
  cursor: pointer;
  transition: color 0.3s ease;
  margin: 0;

  &:hover {
    color: white;
  }

  @media (max-width: 768px) {
    font-size: 1.2rem;
  }
`;

const SliderContainer = styled.div`
  position: relative;
  overflow-x: auto;
  overflow-y: hidden;
  scroll-behavior: smooth;

  &::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none;
  scrollbar-width: none;
`;

const Slider = styled(motion.div)`
  display: flex;
  gap: 0.5rem;
  padding: 1rem 0;
  min-width: max-content;
`;

const SliderButton = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(0, 0, 0, 0.8);
  border: none;
  color: white;
  font-size: 2rem;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  cursor: pointer;
  z-index: 10;
  opacity: 0.7;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: rgba(0, 0, 0, 0.9);
    transform: translateY(-50%) scale(1.1);
    opacity: 1;
  }

  &.prev {
    left: 10px;
  }

  &.next {
    right: 10px;
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

// Global scroll state storage
const scrollStates = {};

const ContentRow = ({
  title,
  content,
  contentIds,
  rowIndex,
  isCustom = false,
  onEdit,
  onDelete,
  onMoveUp,
  onMoveDown,
  canMoveUp,
  canMoveDown
}) => {
  console.log(`[${title}] ContentRow mounting/rerendering, rowIndex:`, rowIndex, 'content length:', content?.length || 0);
  const sliderRef = useRef(null);

  // Initialize scroll state from global storage or 0
  const [translateX, setTranslateX] = React.useState(() => scrollStates[title] || 0);
  const [maxTranslate, setMaxTranslate] = React.useState(0);

  // Update global storage when translateX changes
  React.useEffect(() => {
    scrollStates[title] = translateX;
  }, [translateX, title]);

  // Use direct content if provided, otherwise use contentIds (backward compatibility)
  const rowContent = content || getContentByIds(contentIds || []);
  const itemWidth = 190; // Updated for new portrait card width (180px + 10px gap)
  const [itemsPerView, setItemsPerView] = React.useState(5);

  React.useEffect(() => {
    const updateMaxTranslate = () => {
      const viewportWidth = window.innerWidth;
      const calculatedItemsPerView = Math.floor((viewportWidth * 0.92) / itemWidth);
      setItemsPerView(calculatedItemsPerView);

      if (rowContent.length > calculatedItemsPerView) {
        const totalWidth = rowContent.length * itemWidth;
        const visibleWidth = calculatedItemsPerView * itemWidth;
        const newMaxTranslate = totalWidth - visibleWidth;
        setMaxTranslate(newMaxTranslate);
        console.log(`${title} - Max translate set to:`, newMaxTranslate, 'Items:', rowContent.length);
      } else {
        setMaxTranslate(0);
      }
    };

    updateMaxTranslate();
    window.addEventListener('resize', updateMaxTranslate);
    return () => window.removeEventListener('resize', updateMaxTranslate);
  }, [rowContent.length, itemWidth, title]);

  const maxTranslateRef = useRef(maxTranslate);
  maxTranslateRef.current = maxTranslate;

  // Track scroll position for button visibility
  const [canScrollPrev, setCanScrollPrev] = React.useState(false);
  const [canScrollNext, setCanScrollNext] = React.useState(false);

  const updateScrollButtons = useCallback(() => {
    if (sliderRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = sliderRef.current;
      setCanScrollPrev(scrollLeft > 0);
      setCanScrollNext(scrollLeft < scrollWidth - clientWidth - 1);
    }
  }, []);

  const handlePrevious = useCallback(() => {
    if (sliderRef.current) {
      const scrollAmount = itemWidth * itemsPerView;
      const newScrollLeft = Math.max(0, sliderRef.current.scrollLeft - scrollAmount);
      console.log(`[${title}] Previous clicked - Current:`, sliderRef.current.scrollLeft, 'Moving to:', newScrollLeft, 'Items:', rowContent.length);
      sliderRef.current.scrollTo({ left: newScrollLeft, behavior: 'smooth' });
      // Update button visibility after smooth scroll completes
      setTimeout(updateScrollButtons, 300);
    }
  }, [itemWidth, itemsPerView, title, rowContent.length, updateScrollButtons]);

  const handleNext = useCallback(() => {
    if (sliderRef.current) {
      const scrollAmount = itemWidth * itemsPerView;
      const newScrollLeft = sliderRef.current.scrollLeft + scrollAmount;
      console.log(`[${title}] Next clicked - Current:`, sliderRef.current.scrollLeft, 'Moving to:', newScrollLeft, 'Items:', rowContent.length);
      sliderRef.current.scrollTo({ left: newScrollLeft, behavior: 'smooth' });
      // Update button visibility after smooth scroll completes
      setTimeout(updateScrollButtons, 300);
    }
  }, [itemWidth, itemsPerView, title, rowContent.length, updateScrollButtons]);

  React.useEffect(() => {
    const slider = sliderRef.current;
    if (slider) {
      updateScrollButtons();
      slider.addEventListener('scroll', updateScrollButtons);
      return () => slider.removeEventListener('scroll', updateScrollButtons);
    }
  }, [updateScrollButtons, rowContent.length]);

  if (!rowContent || rowContent.length === 0) {
    return null;
  }

  return (
    <RowContainer>
      <RowHeader $hasControls={isCustom}>
        <RowTitle>{title}</RowTitle>
        {isCustom && (
          <SectionControls
            onEdit={onEdit}
            onDelete={onDelete}
            onMoveUp={onMoveUp}
            onMoveDown={onMoveDown}
            canMoveUp={canMoveUp}
            canMoveDown={canMoveDown}
            alwaysVisible={false}
          />
        )}
      </RowHeader>

      <SliderContainer ref={sliderRef}>
        <Slider
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: rowIndex * 0.05 }}
        >
          {rowContent.map((item, index) => (
            <ThumbnailCard
              key={`${item.id}-${index}`}
              content={item}
              index={index}
            />
          ))}
        </Slider>
      </SliderContainer>
    </RowContainer>
  );
};

// Custom comparison function to prevent unnecessary remounts
const areEqual = (prevProps, nextProps) => {
  const titleSame = prevProps.title === nextProps.title;
  const indexSame = prevProps.rowIndex === nextProps.rowIndex;
  const lengthSame = prevProps.content?.length === nextProps.content?.length;
  const firstSame = prevProps.content?.[0]?.id === nextProps.content?.[0]?.id;
  const lastSame = prevProps.content?.[prevProps.content.length - 1]?.id === nextProps.content?.[nextProps.content.length - 1]?.id;

  const isEqual = titleSame && indexSame && lengthSame && firstSame && lastSame;

  if (!isEqual) {
    console.log(`[${prevProps.title}] Props changed:`, {
      titleSame, indexSame, lengthSame, firstSame, lastSame,
      prevLength: prevProps.content?.length,
      nextLength: nextProps.content?.length,
      prevFirst: prevProps.content?.[0]?.id,
      nextFirst: nextProps.content?.[0]?.id
    });
  }

  return isEqual;
};

export default React.memo(ContentRow, areEqual);